import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { CircularProgress } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AuthContext from "../../../context/auth-context";
import { translateDecisionStatus, decisionStatusColor, colorGreen, colorRed } from "../utils";
import BarsChart from "./charts/BarsChart";
import SankeyChart from "./charts/SankeyChart";
import DayLineChart from "./charts/DayLineChart";
import {
    Header, HeaderLeft, HeaderRight, HeaderActionsRow, Title, ExecToggle, ExecPanel, ExecItem,
    Btn, SegControl, SegBtn, SegNote, Report, Grid4, Grid2, Card, Kpi, CardTitle, CmpCol,
    ChartLegend, LoadingWrap, VariationGrid, VarItem, gray
} from "./styles";


function BacktestReport() {
    const { t } = useTranslation();
    let history = useHistory()
    const { backtest_key, company_key } = useParams()

    let user_data = useContext(AuthContext).user_data
    let roles = user_data.roles
    if (!roles.includes("backtest_result_viewer")) {
        history.push("/backtests-inbox")
    }

    const [pageState, setPageState] = useState({ loading: true, error: false, data: null })
    const [base, setBase] = useState("reais")
    const [execOpen, setExecOpen] = useState(false)

    useEffect(() => {
        let requestConfig = {}
        if (user_data.business_group_key) {
            requestConfig = { headers: { "COMPANY-KEY": company_key } }
        }
        axios.get("/dash/backtest/backtest/" + backtest_key + "/result", requestConfig).then(response => {
            setPageState({ loading: false, error: false, data: response.data })
        }).catch(() => {
            setPageState({ loading: false, error: true, data: null })
        })
    }, [backtest_key, company_key, user_data.business_group_key])

    const formatDate = (date) => {
        if (!date) return "-"
        return user_data.locale === "en"
            ? moment(date).format("MM/DD/YYYY HH:mm")
            : moment(date).format("DD/MM/YYYY HH:mm")
    }

    const formatFilterDate = (date) => {
        if (!date) return "-"
        return user_data.locale === "en"
            ? moment.utc(date).format("MM/DD/YYYY")
            : moment.utc(date).format("DD/MM/YYYY")
    }

    const formatValue = (cents) => {
        const reais = (cents || 0) / 100
        if (reais >= 1000000) return (reais / 1000000).toFixed(2) + " mi"
        return reais.toFixed(2)
    }

    const formatPct = (rate) => Math.round((rate || 0) * 100) + "%"

    const formatVariation = (variationRatio) => {
        if (variationRatio === undefined || variationRatio === null) return "-"
        const percentage = Math.round(variationRatio * 100)
        const sign = percentage > 0 ? "+" : percentage < 0 ? "−" : ""
        return sign + Math.abs(percentage) + "%"
    }

    const variationDirectionClass = (variationRatio) => {
        if (variationRatio > 0) return "up"
        if (variationRatio < 0) return "down"
        return "flat"
    }

    const statusDisplayRank = (status) => {
        const statusColor = decisionStatusColor(status)
        if (statusColor === colorGreen) return 0
        if (statusColor === colorRed) return 2
        return 1
    }

    if (pageState.loading) {
        return (
            <div style={{ width: "auto" }}>
                <Title>{t("Relatório do Backtest")}</Title>
                <LoadingWrap><CircularProgress /></LoadingWrap>
            </div>
        )
    }

    if (pageState.error || !pageState.data) {
        return (
            <div style={{ width: "auto" }}>
                <Title>{t("Relatório do Backtest")}</Title>
                <LoadingWrap>
                    <p className="blueText titleSize">{t("unexpect_condition_message")}</p>
                </LoadingWrap>
            </div>
        )
    }

    const backtest = pageState.data
    const result = backtest.result || {}
    const section = base === "reais" ? (result.against_real || {}) : (result.against_current || {})
    const referenceKey = base === "reais" ? "real" : "current"
    const referenceLabel = base === "reais" ? t("Dados reais") : t("Regra atual")

    const summary = section.summary || {}
    const metrics = summary.metrics || {}
    const referenceRate = metrics[referenceKey]?.approval_rate
    const candidateRate = metrics.candidate?.approval_rate
    const referenceValue = metrics[referenceKey]?.approved_value
    const candidateValue = metrics.candidate?.approved_value
    const variation = summary.variation || {}

    const distribution = section.decision_distribution || {}
    const statuses = Object.keys(distribution).sort((firstStatus, secondStatus) => statusDisplayRank(firstStatus) - statusDisplayRank(secondStatus))

    const kpiCards = statuses.map(status => ({
        status,
        label: t(translateDecisionStatus(status)),
        candidateCount: distribution[status]?.candidate?.count || 0,
        referenceCount: distribution[status]?.[referenceKey]?.count || 0
    }))

    const quantityCategories = statuses.map(status => ({
        key: status,
        label: t(translateDecisionStatus(status)),
        color: decisionStatusColor(status),
        ref: distribution[status]?.[referenceKey]?.count || 0,
        cand: distribution[status]?.candidate?.count || 0
    }))

    const valueCategories = statuses.map(status => ({
        key: status,
        label: t(translateDecisionStatus(status)),
        color: decisionStatusColor(status),
        ref: distribution[status]?.[referenceKey]?.value || 0,
        cand: distribution[status]?.candidate?.value || 0
    }))

    const cascade = section.transition_cascade || {}
    const transitions = {}
    Object.keys(cascade).forEach(transitionKey => {
        const fromToParts = transitionKey.split("_to_")
        if (fromToParts.length !== 2) return
        const [fromStatus, toStatus] = fromToParts
        if (!transitions[fromStatus]) transitions[fromStatus] = {}
        transitions[fromStatus][toStatus] = cascade[transitionKey]
    })

    const referenceTotals = {}
    const candidateTotals = {}
    statuses.forEach(status => {
        referenceTotals[status] = distribution[status]?.[referenceKey]?.count || 0
        candidateTotals[status] = distribution[status]?.candidate?.count || 0
    })

    const perDay = section.quantity_per_day || []
    const dayLabels = perDay.map(day => moment(day.date).format("DD/MM"))
    const referenceApprovedCountSeries = perDay.map(day => (day[referenceKey]?.["automatically_approved"]?.count) || 0)
    const candidateApprovedCountSeries = perDay.map(day => (day.candidate?.["automatically_approved"]?.count) || 0)
    const referenceApprovedValueSeries = perDay.map(day => (day[referenceKey]?.["automatically_approved"]?.value) || 0)
    const candidateApprovedValueSeries = perDay.map(day => (day.candidate?.["automatically_approved"]?.value) || 0)

    const comparisonBarWidths = (referenceAmount, candidateAmount) => {
        const largestAmount = Math.max(referenceAmount, candidateAmount, 1)
        return { reference: (referenceAmount / largestAmount) * 100, candidate: (candidateAmount / largestAmount) * 100 }
    }
    const valueBars = comparisonBarWidths(referenceValue, candidateValue)

    const hasTotal = result.total_cases !== undefined

    const processedCases = result.processed_cases !== undefined ? result.processed_cases : result.total_cases
    const hasDistribution = statuses.length > 0
    const hasValueDistribution = statuses.some(status =>
        distribution[status]?.[referenceKey]?.value !== undefined || distribution[status]?.candidate?.value !== undefined)
    const hasApprovalSummary = referenceRate !== undefined || candidateRate !== undefined
    const hasValueSummary = referenceValue !== undefined || candidateValue !== undefined
    const hasComparison = hasApprovalSummary || hasValueSummary

    const variationRows = statuses
        .map(status => ({
            status,
            label: t(translateDecisionStatus(status)),
            color: decisionStatusColor(status),
            rate: variation[status]?.rate,
            value: variation[status]?.value
        }))
        .filter(row => row.rate !== undefined || row.value !== undefined)
    const hasVariation = variationRows.length > 0
    const hasCascade = hasDistribution && Object.keys(cascade).length > 0
    const hasPerDay = perDay.length > 0
    const hasDayValue = perDay.some(day =>
        day[referenceKey]?.["automatically_approved"]?.value !== undefined || day.candidate?.["automatically_approved"]?.value !== undefined)

    return (
        <div style={{ width: "auto", paddingBottom: "30px" }}>
            <Header>
                <HeaderLeft>
                    <Title>{backtest.name || t("Relatório do Backtest")}</Title>
                    <ExecToggle open={execOpen} onClick={() => setExecOpen(!execOpen)}>
                        {t("Detalhes da execução")}
                        <ExpandMoreIcon className="exec-chevron" />
                    </ExecToggle>
                    {execOpen && (
                        <ExecPanel>
                            <ExecItem>
                                <div className="exec-l">{t("Backtest")}</div>
                                <div className="exec-v">{backtest.name}</div>
                            </ExecItem>
                            <ExecItem>
                                <div className="exec-l">{t("Evento")}</div>
                                <div className="exec-v">{t(backtest.event_type)}</div>
                            </ExecItem>
                            <ExecItem>
                                <div className="exec-l">{t("Criado por")}</div>
                                <div className="exec-v">{backtest.analyst_data?.analyst_name} <span style={{ color: gray, fontWeight: 400 }}>{backtest.analyst_data?.analyst_email}</span></div>
                            </ExecItem>
                            <ExecItem>
                                <div className="exec-l">{t("Criado em")}</div>
                                <div className="exec-v">{formatDate(backtest.created_at)}</div>
                            </ExecItem>
                            <ExecItem>
                                <div className="exec-l">{t("Período")}</div>
                                <div className="exec-v">{formatFilterDate(backtest.filters?.initial_date)} - {formatFilterDate(backtest.filters?.end_date)}</div>
                            </ExecItem>
                            <ExecItem>
                                <div className="exec-l">{t("Casos processados")}</div>
                                <div className="exec-v">{processedCases} / {result.total_cases}</div>
                            </ExecItem>
                        </ExecPanel>
                    )}
                </HeaderLeft>
                <HeaderRight>
                    <HeaderActionsRow>
                        <SegControl>
                            <SegBtn active={base === "reais"} onClick={() => setBase("reais")}>{t("Dados reais")}</SegBtn>
                            <SegBtn active={base === "atual"} onClick={() => setBase("atual")}>{t("Regra atual")}</SegBtn>
                        </SegControl>
                        <Btn onClick={() => history.push("/backtests-inbox")}>{t("Voltar")}</Btn>
                    </HeaderActionsRow>
                    <SegNote>{t("Referência")}: <b>{referenceLabel}</b> × {t("Regra candidata")}</SegNote>
                </HeaderRight>
            </Header>

            <Report>
                {(hasTotal || hasDistribution) && (
                    <Grid4>
                        {hasTotal && (
                            <Kpi>
                                <div className="kpi-n">{result.total_cases || 0}</div>
                                <div className="kpi-l">{t("Casos analisados")}</div>
                            </Kpi>
                        )}
                        {kpiCards.map(card => (
                            <Kpi key={card.status}>
                                <div className="kpi-n">{card.candidateCount}</div>
                                <div className="kpi-l">{card.label}</div>
                                <div className="kpi-delta flat">{referenceLabel}: {card.referenceCount}</div>
                            </Kpi>
                        ))}
                    </Grid4>
                )}

                {hasComparison && (
                    <Card>
                        <CardTitle>
                            {t("Taxa de aprovação e valor liberado")}
                            <span className="muted">{referenceLabel} × {t("Regra candidata")}</span>
                        </CardTitle>
                        <Grid2>
                            {hasApprovalSummary && (
                                <CmpCol>
                                    <div className="cmp-lab">{t("Casos aprovados")}</div>
                                    <div className="cmp-vals">
                                        <span className="cmp-a">{referenceLabel} {formatPct(referenceRate)}</span>
                                        <span>→</span>
                                        <span className="cmp-n">{t("Regra candidata")} {formatPct(candidateRate)}</span>
                                    </div>
                                    <div className="cmpbar"><i className="a" style={{ width: formatPct(referenceRate) }} /></div>
                                    <div className="cmpbar"><i className="n" style={{ width: formatPct(candidateRate) }} /></div>
                                </CmpCol>
                            )}
                            {hasValueSummary && (
                                <CmpCol>
                                    <div className="cmp-lab">{t("Valor liberado")}</div>
                                    <div className="cmp-vals">
                                        <span className="cmp-a">{referenceLabel} {formatValue(referenceValue)}</span>
                                        <span>→</span>
                                        <span className="cmp-n">{t("Regra candidata")} {formatValue(candidateValue)}</span>
                                    </div>
                                    <div className="cmpbar"><i className="a" style={{ width: valueBars.reference + "%" }} /></div>
                                    <div className="cmpbar"><i className="n" style={{ width: valueBars.candidate + "%" }} /></div>
                                </CmpCol>
                            )}
                        </Grid2>
                    </Card>
                )}

                {hasVariation && (
                    <Card>
                        <CardTitle>
                            {t("Variação")}
                            <span className="muted">{t("Regra candidata")} × {referenceLabel}</span>
                        </CardTitle>
                        <VariationGrid>
                            {variationRows.map(row => (
                                <VarItem key={row.status}>
                                    <div className="var-status">
                                        <i className="dot" style={{ background: row.color }} />
                                        {row.label}
                                    </div>
                                    <div className="var-metric">
                                        <span className="var-k">{t("Variação de quantidade")}</span>
                                        <span className={["var-v", variationDirectionClass(row.rate)].join(" ")}>{formatVariation(row.rate)}</span>
                                    </div>
                                    <div className="var-metric">
                                        <span className="var-k">{t("Variação de valor absoluto")}</span>
                                        <span className={["var-v", variationDirectionClass(row.value)].join(" ")}>{formatVariation(row.value)}</span>
                                    </div>
                                </VarItem>
                            ))}
                        </VariationGrid>
                    </Card>
                )}

                {hasDistribution && (
                    <Card>
                        <CardTitle>
                            {t("Distribuição por quantidade")}
                            <span className="muted">{referenceLabel} × {t("regra candidata")} · {t("casos")}</span>
                        </CardTitle>
                        <BarsChart categories={quantityCategories} refLabel={referenceLabel} candLabel={t("Regra candidata")} />
                        <ChartLegend>
                            <span><i className="sq" style={{ background: gray }} />{referenceLabel}</span>
                            <span><i className="sq" style={{ background: "#0b1f82" }} />{t("Regra candidata")}</span>
                        </ChartLegend>
                    </Card>
                )}

                {hasValueDistribution && (
                    <Card>
                        <CardTitle>
                            {t("Distribuição por valor")}
                            <span className="muted">{referenceLabel} × {t("regra candidata")}</span>
                        </CardTitle>
                        <BarsChart categories={valueCategories} refLabel={referenceLabel} candLabel={t("Regra candidata")} format={formatValue} />
                        <ChartLegend>
                            <span><i className="sq" style={{ background: gray }} />{referenceLabel}</span>
                            <span><i className="sq" style={{ background: "#0b1f82" }} />{t("Regra candidata")}</span>
                        </ChartLegend>
                    </Card>
                )}

                {hasCascade && (
                    <Card>
                        <CardTitle>
                            {t("Cascata de transições")}
                            <span className="muted">{referenceLabel} → {t("regra candidata")}</span>
                        </CardTitle>
                        <SankeyChart
                            statuses={statuses}
                            leftTotals={referenceTotals}
                            rightTotals={candidateTotals}
                            transitions={transitions}
                            colorFn={decisionStatusColor}
                            labelFn={(status) => t(translateDecisionStatus(status))}
                            leftHead={referenceLabel}
                            rightHead={t("candidata")}
                        />
                        <ChartLegend>
                            {statuses.map(status => (
                                <span key={status}><i className="sq" style={{ background: decisionStatusColor(status) }} />{t(translateDecisionStatus(status))}</span>
                            ))}
                        </ChartLegend>
                    </Card>
                )}

                {hasPerDay && (
                    <Card>
                        <CardTitle>
                            {t("Casos aprovados — dia a dia")}
                            <span className="muted">{t("Aprovado Automaticamente")}</span>
                        </CardTitle>
                        <DayLineChart days={dayLabels} refSeries={referenceApprovedCountSeries} candSeries={candidateApprovedCountSeries} refLabel={referenceLabel} candLabel={t("Regra candidata")} />
                        <ChartLegend>
                            <span><i className="ll dashed" />{referenceLabel}</span>
                            <span><i className="ll solid" />{t("Regra candidata")}</span>
                        </ChartLegend>
                    </Card>
                )}
                {hasPerDay && hasDayValue && (
                    <Card>
                        <CardTitle>
                            {t("Valor aprovado — dia a dia")}
                            <span className="muted">{t("Aprovado Automaticamente")}</span>
                        </CardTitle>
                        <DayLineChart days={dayLabels} refSeries={referenceApprovedValueSeries} candSeries={candidateApprovedValueSeries} refLabel={referenceLabel} candLabel={t("Regra candidata")} format={formatValue} />
                        <ChartLegend>
                            <span><i className="ll dashed" />{referenceLabel}</span>
                            <span><i className="ll solid" />{t("Regra candidata")}</span>
                        </ChartLegend>
                    </Card>
                )}
            </Report>
        </div>
    )
}

export default BacktestReport
